import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { ActionType, Log } from "../../utils/models/Log";
import { DataGrid } from "aw-data-grid";
import './LogsTable.scss'
import { useTheme } from "../../utils/contexts/ThemeContext";

const LogsGridTable = ({
  logs: logsProp,
  loading,
  pagination,
  setPagination,
  total,
  filters,
  setFilters,
  sorting,
  setSorting,
  manual = false, // client-side per defecte, canvia a true si després fas server-side
  globalFilter,
  onGlobalFilterChange,
  edit = true,
  onRowClick,
}: {
  logs: Log[];
  loading: boolean;
  pagination: { pageIndex: number; pageSize: number };
  setPagination: React.Dispatch<
    React.SetStateAction<{ pageIndex: number; pageSize: number }>
  >;
  total: number; // pageCount quan manual=true
  filters: any;
  setFilters: any;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<any>>;
  manual?: boolean;
  globalFilter?: any;
  onGlobalFilterChange?: React.Dispatch<React.SetStateAction<any>>;
  edit?: boolean;
  onRowClick?: (log: Log) => void;
}) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<Log[]>([]);
  const theme = useTheme();

  const dark = theme.theme === 'dark';

  useEffect(() => {
    setLogs([...logsProp]);
  }, [logsProp]);


  const getActionStyle = (val: ActionType | string | undefined) => {
    const key = typeof val === "number" ? ActionType[val] : val;
    switch (key) {
      case "Create":
        return { color: "bg-success text-white", icon: "bi-plus-circle" };
      case "Update":
        return { color: "bg-warning text-dark", icon: "bi-pencil" };
      case "Delete":
        return { color: "bg-danger text-white", icon: "bi-trash" };
      case "Login":
        return { color: "bg-primary text-white", icon: "bi-person" };
      case "Logout":
        return { color: "bg-secondary text-white", icon: "bi-person" };
      default:
        return { color: "bg-light text-dark", icon: "bi-question" };
    }
  };

  const columnHelper = createColumnHelper<Log>();

  const columns = [
    columnHelper.accessor("idlog", {
      header: t("Identifier"),
      enableSorting: true,
      enableColumnFilter: false,
      cell: info => String(info.getValue() ?? ""),
    }),
    columnHelper.accessor(row => row.user?.name ?? "", {
      id: "user",
      header: t("User"),
      enableSorting: true,
      cell: (info) => <span className="fw-bold">{info.getValue() || ""}</span>,
    }),
    columnHelper.accessor("action", {
      header: t("Action"),
      enableSorting: true,
      meta: {
        filterOptions: [
          { label: "Create", value: ActionType.Create },
          { label: "Update", value: ActionType.Update },
          { label: "Delete", value: ActionType.Delete },
          { label: "Login", value: ActionType.Login },
          { label: "Logout", value: ActionType.Logout },
        ],
      },
      cell: (info) => {
        const val = info.getValue();
        const label = typeof val === "number" ? ActionType[val] : String(val ?? "");
        const { color, icon } = getActionStyle(val);

        return (
          <div
            className={`log-action badge rounded-2 p-2 d-inline-flex align-items-center gap-2 ${color}`}
            style={{ minWidth: "90px" }}>
            <i className={`bi ${icon}`}></i>
            <span>{label}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor("objectType", {
      header: t("ObjectType"),
      enableSorting: true,
      cell: info => String(info.getValue() ?? ""),
    }),
    columnHelper.accessor("objectName", {
      header: t("ObjectName"),
      enableSorting: true,
      cell: info => String(info.getValue() ?? ""),
    }),
    columnHelper.accessor("objectID", {
      header: t("ObjectID"),
      enableSorting: true,
      cell: info => String(info.getValue() ?? ""),
    }),
    columnHelper.accessor("createdAt", {
      header: t("Date"),
      enableSorting: true,
      cell: info => {
        const d = info.getValue() as unknown as string | Date | undefined;
        return d ? new Date(d).toLocaleString() : "";
      },
    }),
  ];

  return (
    <div
      className={`p-2 rounded-1 ${!edit ? "dashboardGridMachine" : "editGridMachine"}`}
      style={{ backgroundColor: "var(--background-secondary)" }}
    >
      {!edit && <p className="mt-1 h4 text-center">{t("Logs")}</p>}

      {loading ? (
        <div className="text-center pt-5">
          <Spinner animation="border" variant="success" />
        </div>
      ) : (
        <DataGrid
          data={logs}
          columns={columns}
          columnFilters={filters}
          onColumnFiltersChange={setFilters}
          pagination={pagination}
          pageCount={total}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          manual={manual} // client-side = false, server-side = true
          filtersPlaceholder={t("Search")}
          globalFilter={globalFilter}
          onGlobalFilterChange={onGlobalFilterChange}
          onRowClick={onRowClick}
          dark={dark}
        />
      )}
    </div>
  );
};

export default LogsGridTable;
